package horcrux.frontend.common.views.auth

import horcrux.common.api.dtos.types.EmailAddress
import horcrux.common.utils.safelet.safeLet
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.clients.AuthenticationClient
import horcrux.frontend.common.value
import io.ktor.util.logging.KtorSimpleLogger
import mui.material.AlertColor
import mui.material.Box
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.FormControlMargin
import mui.material.Grid
import mui.material.GridProps
import mui.material.Link
import mui.material.TextField
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.html.ReactHTML.form
import react.dom.onChange
import web.cssom.px
import web.html.ButtonType
import web.html.InputType

private val logger = KtorSimpleLogger("horcrux.frontend.auth.SignIn")

/**
 * https://github.com/JetBrains/kotlin-wrappers/issues/1856
 */
inline var GridProps.xs: Any?
    get() = asDynamic().xs
    set(value) {
        asDynamic().xs = value
    }

external interface SignInProps : Props {
    var globalAppState: GlobalAppState
    var emailAddressMaybeState: MaybeState<EmailAddress>
}

val SignIn = FC<SignInProps> { props ->
    val showForgotPasswordDialogState = BooleanState()

    ForgotPasswordDialog {
        this.showDialogState = showForgotPasswordDialogState
        this.globalAppState = props.globalAppState
    }

    Box {
        var passwordMaybeState = MaybeState<String>()
        sx {
            this.marginTop = 1.px
        }
        this.component = form
        this.onSubmit = {
            it.preventDefault()
            safeLet(props.emailAddressMaybeState.get(), passwordMaybeState.get()) { email, password ->
                props.globalAppState.submitAuthenticationClientAPIRequest { authenticationClient ->
                    authenticationClient.signIn(
                        email = email,
                        password = password,
                    )
                        .onLeft { cause ->
                            when (cause) {
                                is AuthenticationClient.UnverifiedEmailException -> {}
                                else -> {
                                    logger.error("There was an error signing in", cause)
                                    props.globalAppState.updateBanner(
                                        AlertColor.error,
                                        "There was an error signing in:\n${cause.message}"
                                    )
                                }
                            }
                        }
                        .onRight {
                            props.globalAppState.updateBanner(AlertColor.success, "Successfully signed in!")
                            props.globalAppState.successfulLoginCallback()
                        }.bind()
                }
            }
        }

        TextField {
            this.margin = FormControlMargin.normal
            this.required = true
            this.fullWidth = true
            this.id = "email"
            this.label = ReactNode("Email Address")
            this.type = InputType.email
            this.name = "email"
            this.autoComplete = "email"
            this.autoFocus = true
            this.value = props.emailAddressMaybeState.get()?.value
            this.onChange = { props.emailAddressMaybeState.set(EmailAddress(it.value())) }
        }
        TextField {
            this.margin = FormControlMargin.normal
            this.required = true
            this.fullWidth = true
            this.id = "password"
            this.label = ReactNode("Password")
            this.type = InputType.password
            this.name = "password"
            this.autoComplete = "current-password"
            this.onChange = { passwordMaybeState.set(it.value()) }
        }
        Button {
            this.type = ButtonType.submit
            this.formMethod = "POST"
            this.fullWidth = true
            this.variant = ButtonVariant.contained
            sx {
                this.marginTop = 3.px
                this.marginBottom = 2.px
            }
            +"Sign In"
        }
        Grid {
            this.container = true
            Grid {
                this.item = true
                this.xs = true
                Link {
                    +"Forgot password?"
                    this.variant = "body2"
                    this.onClick = {
                        showForgotPasswordDialogState.set(true)
                    }
                }
            }
        }
    }
}
